/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import REGIONS from "@/data/italyRegions.json";
import MARCHE_GEMME from "@/data/marcheGemme.json";
import { setActiveRegion } from "@/components/itly/SiteBackdrop";
import { useI18n } from "@/i18n/I18nProvider";

const SUBAREAS_META = [
    { id: "all", key: "map.subarea.all", color: "var(--itly-fg-strong)" },
    { id: "conero", key: "map.subarea.conero", color: "#1F6B70" },
    { id: "piceno", key: "map.subarea.piceno", color: "#B8532D" },
    { id: "sibillini", key: "map.subarea.sibillini", color: "#7A3E96" },
    { id: "nord", key: "map.subarea.nord", color: "#5A6E24" },
];

/**
 * Real Italy map — accurate SVG regions from openpolis geojson (simplified).
 * - All 20 regions rendered as separate paths
 * - Marche = highlighted (prima regione)
 * - Sub-area chips filter/dim pins for exploration
 */
function SubareaChips({ t, SUBAREAS, subarea, setSubarea, setActive }) {
    return (
        <div
            data-testid="marche-subareas"
            role="tablist"
            aria-label={t("map.filterAriaLabel")}
            style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 32,
                justifyContent: "center",
            }}
        >
            {SUBAREAS.map((s) => {
                const active = subarea === s.id;
                const count = s.id === "all"
                    ? MARCHE_GEMME.length
                    : MARCHE_GEMME.filter((g) => g.subarea === s.id).length;
                return (
                    <button
                        key={s.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => {
                            setSubarea(s.id);
                            setActive(null);
                        }}
                        data-testid={`marche-subarea-${s.id}`}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "9px 16px",
                            borderRadius: 999,
                            border: `1px solid ${active ? s.color : "var(--itly-border)"}`,
                            background: active ? s.color : "var(--itly-surface)",
                            color: active ? "#ffffff" : "var(--itly-fg-strong)",
                            fontFamily: "Inter, sans-serif",
                            fontSize: 13,
                            fontWeight: active ? 700 : 500,
                            letterSpacing: "0.005em",
                            cursor: "pointer",
                            transition: "background 200ms ease, color 200ms ease, border-color 200ms ease",
                        }}
                    >
                        <span
                            aria-hidden="true"
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                background: active ? "#ffffff" : s.color,
                            }}
                        />
                        {s.label}
                        <span
                            style={{
                                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                                fontSize: 11.5,
                                fontWeight: 600,
                                padding: "1px 7px",
                                borderRadius: 999,
                                background: active ? "rgba(255,255,255,0.16)" : "var(--itly-cream)",
                                color: active ? "#ffffff" : "var(--itly-muted)",
                            }}
                        >
                            {count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/* Zoom on the Marche: region bbox x 187.8–242.5 · y 235.4–275.9 (Italy viewBox 400×640). */
const ZOOM = 5.2;
const VIEW_W = 400;
const VIEW_H = 320;
const TX = VIEW_W / 2 - ZOOM * 215.14;
const TY = VIEW_H / 2 - ZOOM * 255.66;
const NEIGHBOURS = new Set(["Emilia-Romagna", "Toscana", "Umbria", "Lazio", "Abruzzo", "Marche"]);

function ItalyLocator() {
    return (
        <svg
            viewBox="80 150 200 420"
            width="54"
            height="112"
            aria-hidden="true"
            data-testid="italy-locator"
            style={{ display: "block" }}
        >
            {REGIONS.map((r) => (
                <path
                    key={r.name}
                    d={r.path}
                    fill={r.name === "Marche" ? "var(--itly-orange-500)" : "var(--itly-map-region)"}
                    stroke={r.name === "Marche" ? "var(--itly-orange-700)" : "var(--itly-map-stroke)"}
                    strokeWidth="1.2"
                    vectorEffect="non-scaling-stroke"
                />
            ))}
        </svg>
    );
}

function GemPin({ g, isActive, isVisible, setActive }) {
    const x = TX + ZOOM * g.x;
    const y = TY + ZOOM * g.y;
    const labelRight = x < VIEW_W - 90;
    return (
        <g
            transform={`translate(${x.toFixed(2)}, ${y.toFixed(2)})`}
            className="itly-gem-pin"
            style={{
                cursor: isVisible ? "pointer" : "default",
                opacity: isVisible ? 1 : 0.16,
                transition: "opacity 300ms ease",
                pointerEvents: isVisible ? "auto" : "none",
            }}
            onMouseEnter={() => isVisible && setActive(g.id)}
            onFocus={() => isVisible && setActive(g.id)}
            onClick={() => isVisible && setActive(g.id)}
            tabIndex={isVisible ? 0 : -1}
            aria-label={g.nome}
            data-testid={`pin-${g.id}`}
        >
            <circle
                r={isActive ? 15 : 10}
                fill="var(--itly-orange-500)"
                opacity={isActive ? 0.26 : 0.14}
                className="itly-pin-ring"
                style={{ pointerEvents: "none" }}
            />
            <circle
                r={isActive ? 6 : 4.4}
                fill={isActive ? "var(--itly-orange-500)" : "var(--itly-fg-strong)"}
                stroke="#ffffff"
                strokeWidth="1.5"
                style={{ transition: "all 220ms ease" }}
            />
            {isActive && (
                <text
                    x={labelRight ? 12 : -12}
                    y="4"
                    textAnchor={labelRight ? "start" : "end"}
                    fontFamily="Inter, sans-serif"
                    fontSize="11"
                    fontWeight="700"
                    fill="var(--itly-fg-strong)"
                    stroke="var(--itly-cream)"
                    strokeWidth="3"
                    paintOrder="stroke"
                    style={{ pointerEvents: "none" }}
                    data-testid={`pin-label-${g.id}`}
                >
                    {g.nome}
                </text>
            )}
        </g>
    );
}

const CONTEXT_REGIONS = REGIONS.filter((r) => NEIGHBOURS.has(r.name));

function MarcheSvgMap({ t, subarea, active, setActive }) {
    // Active pin last so its label stays on top
    const orderedGems = useMemo(
        () => [...MARCHE_GEMME].sort((a, b) => (a.id === active) - (b.id === active)),
        [active],
    );
    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                maxWidth: 560,
                margin: "0 auto",
            }}
        >
            <svg
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                width="100%"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: "block", overflow: "hidden", borderRadius: 20 }}
                data-testid="marche-zoom-svg"
            >
                <defs>
                    <filter id="itly-drop" x="-10%" y="-10%" width="120%" height="120%">
                        <feDropShadow dx="0" dy="1.5" stdDeviation="1.6" floodColor="#0F3D3E" floodOpacity="0.08" />
                    </filter>
                </defs>

                {/* Adriatic tint */}
                <rect width={VIEW_W} height={VIEW_H} fill="var(--itly-teal-050)" opacity="0.45" />

                <g transform={`translate(${TX.toFixed(3)}, ${TY.toFixed(3)}) scale(${ZOOM})`} filter="url(#itly-drop)">
                    {CONTEXT_REGIONS.map((r) => {
                        const isMarche = r.name === "Marche";
                        return (
                            <path
                                key={r.name}
                                d={r.path}
                                fill={isMarche ? "var(--itly-surface)" : "var(--itly-map-region)"}
                                stroke={isMarche ? "var(--itly-fg-strong)" : "var(--itly-map-stroke)"}
                                strokeWidth={isMarche ? 1.6 : 0.9}
                                strokeLinejoin="round"
                                vectorEffect="non-scaling-stroke"
                                data-testid={`region-${r.name}`}
                            >
                                <title>{r.name}</title>
                            </path>
                        );
                    })}
                </g>

                {/* Title */}
                <g transform="translate(18, 26)">
                    <text
                        fontFamily="Inter, sans-serif"
                        fontSize="12"
                        fontWeight="700"
                        fill="var(--itly-fg-strong)"
                        letterSpacing="0.16em"
                    >
                        MARCHE
                    </text>
                    <text
                        y="15"
                        fontFamily="Inter, sans-serif"
                        fontSize="10"
                        fill="var(--itly-orange-700)"
                        letterSpacing="0.08em"
                    >
                        {t("map.firstRegion")}
                    </text>
                </g>

                {orderedGems.map((g) => (
                        <GemPin
                            key={g.id}
                            g={g}
                            isActive={active === g.id}
                            isVisible={subarea === "all" || g.subarea === subarea}
                            setActive={setActive}
                        />
                    ))}
            </svg>

            {/* Italy locator inset */}
            <div
                style={{
                    position: "absolute",
                    left: 12,
                    bottom: 12,
                    padding: "6px 8px",
                    background: "var(--itly-surface)",
                    border: "1px solid var(--itly-border)",
                    borderRadius: 12,
                    boxShadow: "var(--itly-card-shadow)",
                }}
            >
                <ItalyLocator />
            </div>
        </div>
    );
}

function panelBadge(t, current, subarea, areaMeta) {
    if (current) return t("map.gemmaBadge");
    if (subarea !== "all") return t("map.areaBadge", { area: areaMeta?.label });
    return t("map.hoverPin");
}

function GemPanel({ t, current, active, setActive, subarea, areaMeta, visibleGems }) {
    return (
        <div
            data-testid="gemma-panel"
            className="itly-card"
            style={{
                padding: "32px 32px 28px",
                minHeight: 280,
                position: "relative",
            }}
        >
            <span
                style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "var(--itly-orange-700)",
                }}
            >
                {panelBadge(t, current, subarea, areaMeta)}
            </span>

            {current ? (
                <div
                    key={current.id}
                    className="itly-gemma-detail"
                    data-testid={`gemma-detail-${current.id}`}
                >
                    <h3
                        className="itly-serif"
                        style={{
                            margin: "10px 0 0",
                            fontSize: 40,
                            lineHeight: 1.05,
                            color: "var(--itly-fg-strong)",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {current.nome}
                    </h3>
                    <div
                        style={{
                            marginTop: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            flexWrap: "wrap",
                        }}
                    >
                        <span
                            className="itly-pill"
                            style={{
                                padding: "6px 12px",
                                background: "var(--itly-teal-050)",
                                color: "var(--itly-fg-strong)",
                                fontFamily: "Inter, sans-serif",
                                fontSize: 12.5,
                                fontWeight: 600,
                            }}
                        >
                            ✦ {current.area}
                        </span>
                        <span
                            className="itly-pill"
                            style={{
                                padding: "6px 12px",
                                background: "var(--itly-peach-050)",
                                color: "var(--itly-orange-700)",
                                fontFamily: "Inter, sans-serif",
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                            }}
                        >
                            {t("map.pointsX2")}
                        </span>
                    </div>
                    <p
                        style={{
                            marginTop: 18,
                            fontFamily: "Fraunces, serif",
                            fontSize: 18,
                            lineHeight: 1.5,
                            color: "var(--itly-ink)",
                            fontStyle: "italic",
                        }}
                    >
                        "{current.story}"
                    </p>
                </div>
            ) : (
                <div
                    data-testid="gemma-empty"
                    style={{
                        marginTop: 12,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 15.5,
                        color: "var(--itly-muted)",
                        lineHeight: 1.6,
                    }}
                >
                    {subarea !== "all" ? (
                        <>
                            <strong style={{ color: "var(--itly-fg-strong)" }}>
                                {visibleGems.length} {visibleGems.length === 1 ? t("map.gemmaOne") : t("map.gemmaMany")}
                            </strong>{" "}
                            {t("map.inThisArea")}{" "}
                            {visibleGems.map((g, i) => (
                                <span key={g.id}>
                                    <button
                                        type="button"
                                        onClick={() => setActive(g.id)}
                                        data-testid={`gemma-inline-${g.id}`}
                                        style={{
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                            color: "var(--itly-fg-strong)",
                                            fontWeight: 600,
                                            textDecoration: "underline",
                                            textUnderlineOffset: 3,
                                            textDecorationColor: "var(--itly-orange-500)",
                                            cursor: "pointer",
                                            fontFamily: "inherit",
                                            fontSize: "inherit",
                                        }}
                                    >
                                        {g.nome}
                                    </button>
                                    {i < visibleGems.length - 1 ? ", " : "."}
                                </span>
                            ))}
                            <br />
                            <br />
                            {t("map.doublePoints_a")}
                            <strong style={{ color: "var(--itly-fg-strong)" }}>
                                {t("map.doublePoints_b")}
                            </strong>{t("map.doublePoints_c")}
                        </>
                    ) : (
                        <>
                            {t("map.emptyIntro_a")}
                            <strong style={{ color: "var(--itly-fg-strong)" }}>
                                {t("map.emptyIntro_b")}
                            </strong>
                            {t("map.emptyIntro_c")}
                        </>
                    )}
                </div>
            )}

            <div
                style={{
                    marginTop: 32,
                    paddingTop: 20,
                    borderTop: "1px solid var(--itly-border)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12.5,
                    color: "var(--itly-muted)",
                    gap: 12,
                    flexWrap: "wrap",
                }}
            >
                <span>
                    {active
                        ? t("map.footClick")
                        : t("map.footMove")}
                </span>
                <span>{t("map.footMore")}</span>
            </div>
            <p
                style={{
                    marginTop: 12,
                    marginBottom: 0,
                    fontFamily: "Inter, sans-serif",
                    fontSize: 11.5,
                    color: "var(--itly-muted)",
                    lineHeight: 1.55,
                }}
            >
                {t("map.disclaimer")}
            </p>
        </div>
    );
}

export default function ItalyMap() {
    const { t } = useI18n();
    const SUBAREAS = SUBAREAS_META.map((s) => ({ ...s, label: t(s.key) }));
    const [active, setActive] = useState(null);
    useEffect(() => {
        const gem = MARCHE_GEMME.find((g) => g.id === active);
        setActiveRegion(gem ? gem.region || "Marche" : null);
        return () => setActiveRegion(null);
    }, [active]);
    const [subarea, setSubarea] = useState("all");
    const current = active ? MARCHE_GEMME.find((g) => g.id === active) : null;

    const visibleGems = useMemo(
        () => (subarea === "all" ? MARCHE_GEMME : MARCHE_GEMME.filter((g) => g.subarea === subarea)),
        [subarea],
    );
    const areaMeta = SUBAREAS.find((s) => s.id === subarea);

    return (
        <section
            id="mappa"
            data-testid="italy-map-section"
            style={{
                background: "var(--itly-surface)",
                borderTop: "1px solid var(--itly-border)",
                borderBottom: "1px solid var(--itly-border)",
                padding: "96px 32px",
            }}
        >
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        gap: 32,
                        flexWrap: "wrap",
                        marginBottom: 48,
                    }}
                >
                    <div style={{ maxWidth: 620 }}>
                        <p
                            style={{
                                fontFamily: "Inter, sans-serif",
                                fontSize: 12.5,
                                fontWeight: 700,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: "var(--itly-orange-700)",
                                margin: 0,
                            }}
                        >
                            {t("map.firstRegionLine")}
                        </p>
                        <h2
                            className="itly-serif"
                            style={{
                                marginTop: 14,
                                marginBottom: 0,
                                fontSize: "clamp(34px, 3.6vw, 52px)",
                                lineHeight: 1.08,
                                color: "var(--itly-fg-strong)",
                                letterSpacing: "-0.02em",
                            }}
                        >
                            {t("map.startFromMarche")}
                            <em
                                style={{
                                    fontStyle: "italic",
                                    color: "var(--itly-orange-500)",
                                    fontWeight: 500,
                                }}
                            >
                                {t("map.restIsComing")}
                            </em>
                        </h2>                    </div>
                    <div
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 14.5,
                            color: "var(--itly-muted)",
                            maxWidth: 380,
                            lineHeight: 1.55,
                        }}
                    >
                        {t("map.sub")}
                    </div>
                </div>

                {/* Sub-area chips: filter pins by macro area */}
                <SubareaChips t={t} SUBAREAS={SUBAREAS} subarea={subarea} setSubarea={setSubarea} setActive={setActive} />

                <div
                    className="itly-map-wrap"
                    data-testid="italy-map"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)",
                        gap: 48,
                        alignItems: "center",
                    }}
                >
                    <MarcheSvgMap t={t} subarea={subarea} active={active} setActive={setActive} />

                    <GemPanel t={t} current={current} active={active} setActive={setActive} subarea={subarea} areaMeta={areaMeta} visibleGems={visibleGems} />
                </div>
            </div>
        </section>
    );
}
